// ==================== SUBSCRIPTION SERVICE ====================

import { apiService } from './api';
import type { ApiResponse } from '../types';

// ==================== TYPES ====================

export interface PriceCalculation {
  planAmount: number;
  setupFee: number;
  leadGenAddon: number;
  totalAmount: number;
  recurringAmount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  planName: string;
  setupFeeIncluded: boolean;
  leadGenAddonIncluded: boolean;
}

export interface SetupFeeDetails {
  amount: number;
  enabled: boolean;
}

export interface LeadGenAddonDetails {
  /** Monthly add-on fee for the plan. */
  amount: number;
  enabled: boolean;
}

/** What enabling the add-on costs right now for the current subscription. */
export interface LeadGenAddonQuote {
  monthlyFee: number;
  billingCycle: 'MONTHLY' | 'YEARLY';
  currency: string;
  /** Charged now for the rest of the current period (0 if already paid). */
  chargeNow: number;
  monthsCharged: number;
  /** Added to every renewal while enabled (12 months on a yearly plan). */
  recurringAmount: number;
  periodEnd: string;
  alreadyPaidThisPeriod: boolean;
}

/** Add-on amount billed per cycle: the monthly fee, ×12 on yearly plans. */
export const leadGenAddonCycleAmount = (monthlyFee: number, isYearly: boolean) =>
  Math.round((isYearly ? monthlyFee * 12 : monthlyFee) * 100) / 100;

export interface CheckoutSessionResponse {
  checkoutSessionId: string;
  clientSecret: string;
  subscriptionId: string;
  priceDetails: PriceCalculation;
}

export interface PaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  type: 'CARD' | 'BANK_ACCOUNT' | 'WALLET';
  brand: string | null;
  last4: string;
  expiryMonth: number | null;
  expiryYear: number | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface Subscription {
  id: string;
  status: 'PENDING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'EXPIRED';
  billingCycle: 'MONTHLY' | 'YEARLY';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextBillingDate: string;
  planAmount: number;
  setupFee: number;
  setupFeeIncluded: boolean;
  leadGenAddonEnabled: boolean;
  leadGenAddonAmount: number;
  totalAmount: number;
  recurringAmount: number;
  currency: string;
  autoRenew: boolean;
  plan: {
    id: string;
    name: string;
    description: string | null;
    monthlyPrice: number;
    yearlyPrice: number | null;
  };
  defaultPaymentMethod: PaymentMethod | null;
  payments: Array<{
    id: string;
    amount: number;
    status: string;
    paidAt: string | null;
    createdAt: string;
  }>;
}

// ==================== SERVICE ====================

export const subscriptionService = {
  /**
   * Calculate price for a subscription
   */
  calculatePrice: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    includeSetupFee?: boolean;
    includeLeadGenAddon?: boolean;
  }): Promise<ApiResponse<PriceCalculation>> => {
    const queryParams = new URLSearchParams({
      planId: params.planId,
      billingCycle: params.billingCycle,
      includeSetupFee: String(params.includeSetupFee ?? false),
      includeLeadGenAddon: String(params.includeLeadGenAddon ?? false),
    });
    return apiService.get<PriceCalculation>(`/customer/subscription/calculate-price?${queryParams}`);
  },

  /**
   * Get setup fee details for a specific plan
   */
  getSetupFeeDetails: async (planId?: string): Promise<ApiResponse<SetupFeeDetails>> => {
    const queryParams = planId ? `?planId=${planId}` : '';
    return apiService.get<SetupFeeDetails>(`/customer/subscription/setup-fee${queryParams}`);
  },

  /**
   * Lead Generation add-on (Lead Ops) — amount / availability from selected plan (DB), same pattern as setup fee.
   */
  getLeadGenAddonDetails: async (planId?: string): Promise<ApiResponse<LeadGenAddonDetails>> => {
    const queryParams = planId ? `?planId=${planId}` : '';
    return apiService.get<LeadGenAddonDetails>(`/customer/subscription/lead-gen-addon${queryParams}`);
  },

  /**
   * Create a checkout session
   */
  createCheckout: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    includeSetupFee?: boolean;
    includeLeadGenAddon?: boolean;
  }): Promise<ApiResponse<CheckoutSessionResponse>> => {
    return apiService.post<CheckoutSessionResponse>('/customer/subscription/checkout', params);
  },

  /**
   * Handle payment success callback
   */
  handlePaymentSuccess: async (sessionId: string): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/payment-success', { sessionId });
  },

  /**
   * Get current subscription
   */
  getCurrentSubscription: async (): Promise<ApiResponse<Subscription | null>> => {
    return apiService.get<Subscription | null>('/customer/subscription/current');
  },

  /**
   * Cancel subscription
   */
  cancelSubscription: async (reason?: string): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/cancel', { reason });
  },

  /**
   * Resume a cancelled subscription (before period ends)
   */
  resumeSubscription: async (): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/resume');
  },

  /**
   * Get payment methods
   */
  getPaymentMethods: async (): Promise<ApiResponse<PaymentMethod[]>> => {
    return apiService.get<PaymentMethod[]>('/customer/subscription/payment-methods');
  },

  /**
   * Add a new payment method
   */
  addPaymentMethod: async (params: {
    paymentMethodId: string;
    setAsDefault?: boolean;
  }): Promise<ApiResponse<PaymentMethod>> => {
    return apiService.post<PaymentMethod>('/customer/subscription/payment-methods', params);
  },

  /**
   * Set default payment method
   */
  setDefaultPaymentMethod: async (paymentMethodId: string): Promise<ApiResponse<void>> => {
    return apiService.post<void>(`/customer/subscription/payment-methods/${paymentMethodId}/default`);
  },

  /**
   * Remove a payment method
   */
  removePaymentMethod: async (paymentMethodId: string): Promise<ApiResponse<void>> => {
    return apiService.delete<void>(`/customer/subscription/payment-methods/${paymentMethodId}`);
  },

  /**
   * Create setup intent for adding new payment method
   */
  createSetupIntent: async (): Promise<ApiResponse<{ clientSecret: string }>> => {
    return apiService.post<{ clientSecret: string }>('/customer/subscription/payment-methods/setup-intent');
  },

  /**
   * Get payment history
   */
  getPaymentHistory: async (params?: { page?: number; limit?: number }): Promise<ApiResponse<PaymentHistoryResponse>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    const query = queryParams.toString() ? `?${queryParams}` : '';
    return apiService.get<PaymentHistoryResponse>(`/customer/subscription/payments${query}`);
  },

  /**
   * Get single payment details
   */
  getPaymentDetails: async (paymentId: string): Promise<ApiResponse<PaymentDetails>> => {
    return apiService.get<PaymentDetails>(`/customer/subscription/payments/${paymentId}`);
  },

  /**
   * Get invoice for a payment
   */
  getInvoice: async (paymentId: string): Promise<ApiResponse<Invoice>> => {
    return apiService.get<Invoice>(`/customer/subscription/payments/${paymentId}/invoice`);
  },

  /**
   * Toggle auto-renewal
   */
  toggleAutoRenew: async (autoRenew: boolean): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/auto-renew', { autoRenew });
  },

  /**
   * What enabling the Lead Generation add-on would charge now for the current subscription.
   */
  getLeadGenAddonQuote: async (): Promise<ApiResponse<LeadGenAddonQuote>> => {
    return apiService.get<LeadGenAddonQuote>('/customer/subscription/lead-gen-addon/quote');
  },

  /**
   * Enable or disable the Lead Generation add-on (priced monthly).
   * Enabling charges the rest of the current period now (see getLeadGenAddonQuote),
   * then the add-on is billed with every renewal.
   */
  toggleLeadGenAddon: async (
    enabled: boolean,
    paymentMethodId?: string,
  ): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/lead-gen-addon', {
      enabled,
      ...(paymentMethodId && { paymentMethodId }),
    });
  },

  /**
   * Upgrade/downgrade plan (one-click payment with saved payment method)
   */
  upgradePlan: async (params: {
    planId: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    paymentMethodId: string;
  }): Promise<ApiResponse<Subscription>> => {
    return apiService.post<Subscription>('/customer/subscription/upgrade', params);
  },
};

// ==================== ADDITIONAL TYPES ====================

export interface PaymentDetails {
  id: string;
  amount: number;
  planAmount: number | null;
  setupFee: number | null;
  leadGenAddonFee: number | null;
  currency: string;
  status: string;
  type: 'SUBSCRIPTION' | 'RENEWAL' | 'UPGRADE' | 'ADDON' | 'MANUAL';
  stripePaymentIntentId: string | null;
  paidAt: string | null;
  createdAt: string;
  paymentMethod: PaymentMethod | null;
  /** Snapshot at time of payment (use for display so history shows correct plan) */
  planName?: string | null;
  billingCycle?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  subscription: {
    id: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    plan: {
      id: string;
      name: string;
      description: string | null;
    } | null;
  };
}

export interface PaymentHistoryResponse {
  items: PaymentDetails[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Invoice {
  invoiceNumber: string;
  invoiceDate: string;
  paidDate: string | null;
  status: string;
  customer: {
    name: string;
    email: string;
    address: string;
    phone: string;
  };
  company: {
    name: string;
    tagline: string;
    website: string;
    email: string;
  };
  plan: {
    name: string;
    billingCycle: 'MONTHLY' | 'YEARLY';
    description: string;
  };
  lineItems: Array<{
    description: string;
    amount: number;
    type?: 'subscription' | 'setup' | 'addon';
  }>;
  subtotal: number;
  total: number;
  currency: string;
  formatted: {
    subtotal: string;
    total: string;
  };
  paymentMethod: {
    type: string;
    brand: string | null;
    last4: string;
    expiryMonth?: number | null;
    expiryYear?: number | null;
  } | null;
  paymentId: string;
  paymentType: 'SUBSCRIPTION' | 'RENEWAL' | 'UPGRADE' | 'SETUP_FEE' | 'ADDON' | 'MANUAL';
  paymentMode: string | null;
  paymentDescription: string | null;
  stripePaymentIntentId: string | null;
  stripeReceiptUrl: string | null;
}

export default subscriptionService;

